import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  fetchMyRentLeases,
  formatRentAmount,
  formatRentDate,
  getRentStatusInfo,
} from '../../Services/rentService';
import '../../Components/Lease/LeaseAgreement.css';

const imageUrl = (path) => (window.getPublicUrl ? window.getPublicUrl(path) : path);

const MyLeases = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [leases, setLeases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadLeases = useCallback(async () => {
    setIsLoading(true);
    try {
      const rows = await fetchMyRentLeases();
      setError(null);
      setLeases(rows);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load leases.';
      console.error('Error loading rent leases:', err);
      setError(message);
      setLeases([]);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeases();
  }, [loadLeases]);

  // The endpoint takes no query params, so the search filters the loaded rows.
  const query = searchQuery.trim().toLowerCase();
  const filteredLeases = query
    ? leases.filter((lease) =>
        [
          lease.propertyAddress,
          lease.propertyCityState,
          lease.propertyCode,
          lease.landlordName,
          getRentStatusInfo(lease.status).label,
        ]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(query))
      )
    : leases;

  // The detail page loads its history by property_id and reuses the row it is
  // handed, so it only re-fetches when opened directly.
  const handleViewDetail = (lease) => {
    if (!lease.propertyId) {
      toast.info('Property details are not available for this lease.');
      return;
    }
    navigate(`/tenant/leases/${lease.propertyId}`, { state: { lease } });
  };

  const renderBody = () => {
    if (isLoading) {
      return <div className="leases-placeholder">Loading leases...</div>;
    }
    if (error) {
      return <div className="leases-placeholder leases-placeholder--error">{error}</div>;
    }
    if (filteredLeases.length === 0) {
      return (
        <div className="leases-placeholder">
          {query
            ? 'No leases match your search.'
            : 'No leases yet. Subscribe to monthly rent from a property and it will appear here.'}
        </div>
      );
    }

    return (
      <table className="screenings-table tenant-leases-table">
        <thead>
          <tr>
            <th>Sr. no.</th>
            <th>Property</th>
            <th>Landlord</th>
            <th>Monthly Rent</th>
            <th>Start Date</th>
            <th>Next Payment</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {filteredLeases.map((lease, index) => {
            const statusInfo = getRentStatusInfo(lease.status);
            return (
              <tr key={lease.id ?? `${lease.propertyId}-${index}`}>
                <td>{index + 1}</td>
                <td className="screenings-cell-address">
                  <span className="screenings-cell-address-text" title={lease.propertyAddress}>
                    {lease.propertyAddress || '—'}
                  </span>
                  {lease.propertyCode ? (
                    <span className="leases-cell-sub">Code: {lease.propertyCode}</span>
                  ) : lease.propertyCityState ? (
                    <span className="leases-cell-sub">{lease.propertyCityState}</span>
                  ) : null}
                </td>
                <td className="screenings-cell-address">
                  <span className="screenings-cell-address-text">{lease.landlordName || '—'}</span>
                </td>
                <td>{formatRentAmount(lease.monthlyRent)}</td>
                <td>{formatRentDate(lease.startDate)}</td>
                <td>{formatRentDate(lease.nextPaymentDate)}</td>
                <td>
                  <span className={`lease-badge lease-badge--${statusInfo.variant}`}>
                    {statusInfo.label}
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    className="screenings-view-detail-button"
                    onClick={() => handleViewDetail(lease)}
                    disabled={!lease.propertyId}
                  >
                    View detail
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="screenings-page">
      <div className="screenings-actions-bar">
        <div className="screenings-search-wrapper">
          <img
            src={imageUrl('/assets/images/SearchIcon.svg')}
            alt="Search"
            className="screenings-search-icon"
          />
          <input
            type="text"
            className="screenings-search-input"
            placeholder="Search by property or landlord"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <h2 className="screenings-title">
        My Leases{!isLoading && !error ? ` (${filteredLeases.length})` : ''}
      </h2>

      <div className="screenings-content">{renderBody()}</div>
    </div>
  );
};

export default MyLeases;
